import styled, { css } from 'styled-components';
import { Select } from '@base-ui/react/select';
import { actionColor, actionLight, colors, spacing } from '../../style';
import { inputBorderColor } from '../../style/theme-helpers';
import { sizes as textSizes } from '../text/style';
import type { SelectSize, SelectVariant } from './select';

const triggerVariants = {
  default: css`
    background-color: ${colors.white};
    border: 1px solid ${inputBorderColor};

    &[data-popup-open]:not([disabled]),
    &:focus-within {
      border: 1px solid ${actionColor};
      outline: none;
    }

    &[data-disabled] {
      background: ${colors.gray50};
      cursor: not-allowed;
    }
  `,
  clean: css`
    background-color: transparent;
    border: none;
    color: ${colors.gray900};

    &:focus-visible:not([disabled]) {
      outline: 1px solid ${actionColor};
      border-radius: ${spacing.xxs};
    }

    &[data-disabled] {
      cursor: not-allowed;
    }
  `,
};

const triggerSizes = {
  xs: css`
    height: 28px;
    ${textSizes.xs}
  `,
  sm: css`
    height: ${spacing.lg};
    ${textSizes.sm}
  `,
  md: css`
    height: ${spacing.lg};
    ${textSizes.md}
  `,
  lg: css`
    height: ${spacing.xl};
    ${textSizes.md}
  `,
};

export const StyledTrigger = styled(Select.Trigger)<{
  variant?: SelectVariant;
  size?: SelectSize;
  $minWidth?: string;
  $block?: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  ${props =>
    props.$minWidth &&
    css`
      min-width: ${props.$minWidth};
    `}
  ${props =>
    props.$block &&
    css`
      width: 100%;
    `}
  padding-left: ${spacing.xs};
  padding-right: ${spacing.xs};
  border-radius: 2px;
  color: ${colors.gray900};
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  gap: ${spacing.xs};
  text-align: start;

  ${props => triggerVariants[props.variant || 'default']}
  ${props => triggerSizes[props.size || 'lg']}
  
  &:disabled,
  &[data-disabled] {
    color: ${colors.gray600};
    cursor: not-allowed;
  }
`;

export const StyledValue = styled(Select.Value)`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &[data-placeholder] {
    color: ${colors.gray600};
  }
`;

export const StyledIcon = styled(Select.Icon)`
  display: flex;
  transition: transform 150ms ease-out;

  [data-popup-open] & {
    transform: rotate(180deg);
  }
`;

export const StyledPositioner = styled(Select.Positioner)`
  outline: none;
  z-index: 9999;
`;

export const StyledPopup = styled(Select.Popup)<{ variant?: SelectVariant }>`
  outline: none;
  overflow: hidden;
  background-color: ${colors.white};
  border-radius: 2px;
  box-shadow:
    0 4px 6px -2px rgba(16, 24, 40, 0.03),
    0 12px 16px -4px rgba(16, 24, 40, 0.08);
  width: var(--anchor-width);
  max-width: var(--available-width);
  transform-origin: var(--transform-origin);
  transition:
    opacity 0.1s,
    transform 0.1s;

  &[data-starting-style],
  &[data-ending-style] {
    opacity: 0;
    transform: scale(0.95);
  }

  &[data-side='none'] {
    transition: none;
    transform: none;
    opacity: 1;
  }
`;

export const StyledList = styled(Select.List)`
  padding: ${spacing.xs};
  max-height: 180px;
  overflow-y: auto;
`;

export const StyledSeparator = styled(Select.Separator)`
  height: 1px;
  background: ${colors.gray300};
  margin: ${spacing.xxs} ${spacing.sm};
`;

const itemSizes = {
  xs: css`
    ${textSizes.xs}
  `,
  sm: css`
    ${textSizes.sm}
  `,
  md: css`
    ${textSizes.md}
  `,
  lg: css`
    ${textSizes.md}
  `,
};

export const StyledItem = styled(Select.Item)<{ size?: SelectSize }>`
  position: relative;
  display: flex;
  width: 100%;
  align-items: center;
  white-space: normal;
  overflow-wrap: anywhere;
  padding: ${spacing.xs};
  border-radius: ${spacing.xxs};
  color: ${colors.gray900};
  cursor: pointer;
  user-select: none;
  outline: none;
  transition: background-color 150ms ease-out;

  .item-text {
    flex: 1;
  }

  ${props => itemSizes[props.size || 'lg']}

  &:hover,
  &[data-highlighted] {
    background-color: ${colors.gray100};
  }

  &[data-selected] {
    background-color: ${actionLight};
    color: ${actionColor};
  }

  &[data-disabled] {
    background: transparent;
    color: ${colors.gray400};
    cursor: not-allowed;
  }
`;
